var _html = require('./html')._html;
var _vars = require('./vars')._vars;
var difyApi = require('./providers/dify/api');
var getCurrentProvider = require('./health-check').getCurrentProvider;
var aiGating = require('./ai-gating');
var ads = require('./ads');
var copyConversation = require('./copy-conversation');
var autoScrolledOnce = false;

// ============================================================================
// ANALYTICS: State and Attributes Management
// ============================================================================

// Internal state for analytics attributes
var analyticsState = {
    // API-sourced attributes
    messageIntent: 'no-val',
    leadConceptId: 'no-val',
    threadId: 'no-val',
    runId: 'no-val',
    
    // Frontend-derived attributes
    queryWordCount: 'no-val',
    responseWordCount: 'no-val',
    responseTimeFirstToken: 'no-val'
    // Note: msgSubmitMethod is not stored in analyticsState, it is computed live from
    // _vars.querySource via getMsgSubmitMethod() at the moment each analytics event fires.
};

/**
 * Set metadata attributes from API (thread_id, run_id)
 */
function setMetadataAttributes(metadata) {
    if (metadata && metadata.thread_id) {
        analyticsState.threadId = metadata.thread_id;
    }
    if (metadata && metadata.run_id) {
        analyticsState.runId = metadata.run_id;
    }
}

/**
 * Set metrics attributes from API (message_intent, lead_concept_id)
 * Note: lead_concept_id is only stored for full-width landing pages
 * @param {Object} metrics - Metrics object with intent and/or concept_id
 * @param {boolean} forceSet - If true, bypasses isFullPage check (used for PDQ initialization)
 */
function setMetricsAttributes(metrics, forceSet) {
    if (metrics && metrics.intent) {
        analyticsState.messageIntent = metrics.intent;
    }
    
    // Store lead_concept_id for all page types (removed full-width landing page restriction)
    // OR when forceSet is true (for PDQ initialization in bindui.js)
    // For PDQ queries, protect the URL-sourced leadConceptId from being overwritten by API.
    if (metrics && metrics.concept_id) {
        var conceptId = metrics.concept_id;
        var trimmed = conceptId;
        
        if (typeof conceptId === 'string') {
            trimmed = conceptId.trim();
            if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || 
                (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
                trimmed = trimmed.slice(1, -1).trim();
            }
        }
        
        var isValid = conceptId && 
                     typeof conceptId === 'string' && 
                     trimmed !== '' && 
                     trimmed.length > 0 &&
                     !/^\s*$/.test(trimmed) &&
                     trimmed.toLowerCase() !== 'null' && 
                     trimmed.toLowerCase() !== 'none' && 
                     trimmed.toLowerCase() !== 'undefined' &&
                     trimmed !== '0' &&
                     /^\d+$/.test(trimmed);
        
        if (isValid) {
            // For PDQ queries, protect the URL-sourced leadConceptId from being overwritten by API.
            // _vars.isUrlPredefinedQuery stays true throughout PDQ stream (reset at stream end in dify/index.js).
            // The forceSet path (bindui.js init) is always allowed through.
            if (_vars.isUrlPredefinedQuery && !forceSet) {
                return;
            }
            analyticsState.leadConceptId = trimmed;
        }
    }
}

/**
 * Set query word count
 */
function setQueryWordCount(question) {
    if (!question || typeof question !== 'string') {
        analyticsState.queryWordCount = 'no-val';
        return;
    }
    var words = question.trim().split(/\s+/).filter(function(word) { return word.length > 0; });
    analyticsState.queryWordCount = words.length.toString();
}

/**
 * Set response word count
 */
function setResponseWordCount(answerHtml) {
    if (!answerHtml || typeof answerHtml !== 'string') {
        analyticsState.responseWordCount = 'no-val';
        return;
    }
    var textOnly = answerHtml.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&[^;]+;/g, '').replace(/\s+/g, ' ').trim();
    var words = textOnly.split(/\s+/).filter(function(word) { return word.length > 0; });
    analyticsState.responseWordCount = words.length.toString();
}

/**
 * Set response time to first token
 */
function setResponseTimeFirstToken() {
    if (_vars.searchResult && _vars.searchResult.responseTimeFirstToken) {
        analyticsState.responseTimeFirstToken = _vars.searchResult.responseTimeFirstToken.toString();
    } else {
        analyticsState.responseTimeFirstToken = 'no-val';
    }
}

/**
 * Map _vars.querySource to the standardised msg-sbmt tracking value.
 * Called at the moment each analytics event fires so the value reflects
 * the current query's submission method.
 *
 * Mapping (per ticket):
 *   'PDQ': 'pdq' (pre-defined query from URL)
 *   'Initial prompt': 'initial' (pre-defined prompt clicked on page)
 *   'Follow up question': 'flwup' (follow-up question from AI response)
 *   'ai-mode-query': 'ai-mode-query' (site-search → AI mode)
 *   'Organic' / other: 'organic' (manually typed query)
 */
function getMsgSubmitMethod() {
    var source = _vars.querySource;
    if (source === 'PDQ') return 'pdq';
    if (source === 'Initial prompt') return 'initial';
    if (source === 'Follow up question') return 'flwup';
    if (source === 'ai-mode-query') return 'ai-mode-query';
    return 'organic';
}

/**
 * Get all analytics attributes
 */
function getAnalyticsAttributes() {
    return {
        messageIntent: analyticsState.messageIntent || 'no-val',
        leadConceptId: analyticsState.leadConceptId || 'no-val',
        threadId: analyticsState.threadId || 'no-val',
        runId: analyticsState.runId || 'no-val',
        queryWordCount: analyticsState.queryWordCount || 'no-val',
        responseWordCount: analyticsState.responseWordCount || 'no-val',
        responseTimeFirstToken: analyticsState.responseTimeFirstToken || 'no-val'
        // msgSubmitMethod is not stored in analyticsState; computed live by getMsgSubmitMethod()
    };
}

/**
 * Reset all analytics attributes
 */
function resetAnalyticsAttributes() {
    analyticsState = {
        messageIntent: 'no-val',
        leadConceptId: 'no-val',
        threadId: 'no-val',
        runId: 'no-val',
        queryWordCount: 'no-val',
        responseWordCount: 'no-val',
        responseTimeFirstToken: 'no-val'
    };
}

/**
 * Reset query-specific attributes
 */
function resetQueryAttributes() {
    analyticsState.queryWordCount = 'no-val';
    analyticsState.responseWordCount = 'no-val';
    analyticsState.responseTimeFirstToken = 'no-val';
}

// ============================================================================
// ANALYTICS: Adobe Variable Building and Context Setting
// ============================================================================

/**
 * Build wb.cstmattr string
 * Format: [attributename]value|[attributename]value (no spaces in syntax)
 */
function buildCstmAttr(attrs) {
    if (!attrs || typeof attrs !== 'object') {
        attrs = getAnalyticsAttributes();
    }
    return '[msg-intent]' + (attrs.messageIntent || 'no-val') + '|' +
           '[qry-wrd-cnt]' + (attrs.queryWordCount || 'no-val') + '|' +
           '[re-wrd-cnt]' + (attrs.responseWordCount || 'no-val') + '|' +
           '[res-time]' + (attrs.responseTimeFirstToken || 'no-val') + '|' +
           '[msg-sbmt]' + (attrs.msgSubmitMethod || 'no-val');
}

/**
 * Build wb.cstmids string
 * MEDAIS-2052: Includes qid (frontend-generated QUERYID)
 * Only includes available identifiers (no 'no-val')
 * Returns null if no identifiers are available
 */
function buildCstmIds(attrs) {
    if (!attrs || typeof attrs !== 'object') {
        attrs = getAnalyticsAttributes();
    }
    
    var threadId = attrs.threadId;
    var runId = attrs.runId;
    var queryId = _vars.queryId; // MEDAIS-2052: Get queryId from _vars
    
    // Check if values are actually available (not 'no-val', null, undefined, or empty)
    var hasThreadId = threadId && threadId !== 'no-val' && threadId.trim() !== '';
    var hasRunId = runId && runId !== 'no-val' && runId.trim() !== '';
    var hasQueryId = queryId && queryId.trim() !== ''; // MEDAIS-2052: Check queryId availability
    
    // If none are available, return null (don't pass cstmids at all)
    if (!hasThreadId && !hasRunId && !hasQueryId) {
        return null;
    }
    
    // Build string with only available identifiers
    // Format: [attributename]value|[attributename]value (no spaces in syntax)
    var parts = [];
    if (hasThreadId) {
        parts.push('[thread-id]' + threadId);
    }
    if (hasRunId) {
        parts.push('[run-id]' + runId);
    }
    if (hasQueryId) {
        parts.push('[qid]' + queryId); // MEDAIS-2052: Add qid to cstmids
    }
    
    return parts.join('|');
}

/**
 * Set all Adobe Analytics context variables
 */
function setAnalyticsContext(options) {
    options = options || {};
    var attrs = getAnalyticsAttributes();
    
    // Apply overrides
    if (options.messageIntent !== undefined) attrs.messageIntent = options.messageIntent;
    if (options.queryWordCount !== undefined) attrs.queryWordCount = options.queryWordCount;
    if (options.responseWordCount !== undefined) attrs.responseWordCount = options.responseWordCount;
    if (options.responseTimeFirstToken !== undefined) attrs.responseTimeFirstToken = options.responseTimeFirstToken;
    if (options.threadId !== undefined) attrs.threadId = options.threadId;
    if (options.runId !== undefined) attrs.runId = options.runId;
    if (options.leadConceptId !== undefined) attrs.leadConceptId = options.leadConceptId;
    if (options.msgSubmitMethod !== undefined) attrs.msgSubmitMethod = options.msgSubmitMethod;

    var cstmAttrValue = buildCstmAttr(attrs);
    var cstmIdsValue = buildCstmIds(attrs); // Returns null if no identifiers available
    var ldConceptIdValue = attrs.leadConceptId || 'no-val';
    
    if (typeof wmdSetContext !== 'undefined') {
        // Always set cstmattr (uses 'no-val' for missing values)
        wmdSetContext('wb.cstmattr', cstmAttrValue);
        
        // Only set cstmids if at least one identifier is available
        if (cstmIdsValue !== null) {
            wmdSetContext('wb.cstmids', cstmIdsValue);
        }
        
        // Only set ldcncptid if it has a valid value
        if (ldConceptIdValue !== 'no-val') {
            wmdSetContext('wb.ldcncptid', ldConceptIdValue);
        }
        
        if (typeof addLinkTrackVars !== 'undefined') {
            addLinkTrackVars('wb.cstmattr');
            
            // Only add cstmids to link tracking if it was set
            if (cstmIdsValue !== null) {
                addLinkTrackVars('wb.cstmids');
            }
            
            if (ldConceptIdValue !== 'no-val') {
                addLinkTrackVars('wb.ldcncptid');
            }
        }
    }
    return { cstmattr: cstmAttrValue, cstmids: cstmIdsValue, ldconcptid: ldConceptIdValue };
}

/**
 * Remove Adobe Analytics context variables
 */
function removeAnalyticsContext() {
    if (typeof wmdRemContext !== 'undefined') {
        wmdRemContext('wb.cstmattr');
        wmdRemContext('wb.cstmids');
        wmdRemContext('wb.ldcncptid');
    }
    // Also remove from link tracking variables to prevent persistence
    if (typeof remLinkTrackVars !== 'undefined') {
        remLinkTrackVars('wb.cstmattr');
        remLinkTrackVars('wb.cstmids');
        remLinkTrackVars('wb.ldcncptid');
    }
}

/**
 * Wrapper function to execute analytics calls with context management
 * Sets context before the call and removes it immediately after
 * Uses setTimeout for msg-pdq-submit to avoid scanOmniVars interference
 */
function withAnalyticsContext(eventType, analyticsFunction, options) {
	if (typeof window.firstPvDone != 'undefined') {
		options = options || {};		
		// For msg-pdq-submit, use setTimeout to ensure it runs after any scanOmniVars calls
		if (eventType === 'msg-pdq-submit') {
			setTimeout(function() {
				// Set context before the analytics call
				setContextForEvent(eventType, options);
				
				// Execute the analytics function
				if (typeof analyticsFunction === 'function') {
					analyticsFunction();
				}
				
				// Remove context immediately after the call
				// removeAnalyticsContext();
			}, 150);
		} else {
			// Set context before the analytics call
			setContextForEvent(eventType, options);
			
			// Execute the analytics function
			if (typeof analyticsFunction === 'function') {
				analyticsFunction();
			}
			
			// Remove context immediately after the call
			// removeAnalyticsContext();
		}
		
	} else {
		document.addEventListener('afterPageView', function() { 
			withAnalyticsContext(eventType, analyticsFunction, options);
		})
	}
}

/**
 * Set analytics context for specific event types
 */
function setContextForEvent(eventType, options) {
    options = options || {};
    
    switch (eventType) {
        case 'msg-pdq-submit':
            // Always set queryWordCount from qst (question) if available, not dependent on API metrics
            if (options.qst && typeof options.qst === 'string') {
                setQueryWordCount(options.qst);
            }
            setAnalyticsContext({
                queryWordCount: getAnalyticsAttributes().queryWordCount,
                responseWordCount: 'no-val',
                responseTimeFirstToken: 'no-val',
                messageIntent: 'no-val',
                threadId: options.threadId || 'no-val',
                runId: options.runId || 'no-val',
                leadConceptId: options.leadConceptId,
                msgSubmitMethod: options.msgSubmitMethod !== undefined ? options.msgSubmitMethod : getMsgSubmitMethod()
            });
            break;
        case 'msg-submit':
        case 'msg-submit-clkc':
        case 'deep-dive-clkc':
            setAnalyticsContext({
                queryWordCount: options.queryWordCount,
                responseWordCount: 'no-val',
                responseTimeFirstToken: 'no-val',
                messageIntent: options.messageIntent || 'no-val',
                threadId: options.threadId,
                runId: options.runId,
                leadConceptId: options.leadConceptId,
                msgSubmitMethod: options.msgSubmitMethod !== undefined ? options.msgSubmitMethod : getMsgSubmitMethod()
            });
            break;
        case 'msg-sent':
            setAnalyticsContext({
                queryWordCount: options.queryWordCount,
                responseWordCount: 'no-val',
                responseTimeFirstToken: 'no-val',
                messageIntent: options.messageIntent,
                threadId: options.threadId,
                runId: options.runId,
                leadConceptId: options.leadConceptId,
                msgSubmitMethod: options.msgSubmitMethod !== undefined ? options.msgSubmitMethod : getMsgSubmitMethod()
            });
            break;
        case 'msg-rcvd-frst-wrd':
            setAnalyticsContext({
                queryWordCount: options.queryWordCount,
                responseWordCount: options.responseWordCount || 'no-val',
                responseTimeFirstToken: options.responseTimeFirstToken,
                messageIntent: options.messageIntent,
                threadId: options.threadId,
                runId: options.runId,
                leadConceptId: options.leadConceptId,
                msgSubmitMethod: options.msgSubmitMethod !== undefined ? options.msgSubmitMethod : getMsgSubmitMethod()
            });
            break;
        case 'msg-rcvd':
            setAnalyticsContext({
                queryWordCount: options.queryWordCount,
                responseWordCount: options.responseWordCount,
                responseTimeFirstToken: options.responseTimeFirstToken,
                messageIntent: options.messageIntent,
                threadId: options.threadId,
                runId: options.runId,
                leadConceptId: options.leadConceptId,
                msgSubmitMethod: options.msgSubmitMethod !== undefined ? options.msgSubmitMethod : getMsgSubmitMethod()
            });
            break;
        case 'scroll':
        default:
            // For all subsequent events (such as scroll pageviews, etc.), tracking should continue using the latest available wb.cstmattr and wb.cstmids values,
            // unless those values are overwritten by the next user query submission.
            // https://youtrack.internetbrands.com/issue/MEDAIS-2168#focus=Comments-4-3585081.0-0
            setAnalyticsContext(Object.assign({}, options, {
                msgSubmitMethod: options.msgSubmitMethod !== undefined ? options.msgSubmitMethod : getMsgSubmitMethod()
            }));
            break;
    }
}

/**
 * Verify Adobe variables
 */
function verifyAdobeVariables() {
    if (typeof s_md !== 'undefined') {
        console.log('[Analytics] s_md.wb.cstmattr:', s_md.wb && s_md.wb.cstmattr);
        console.log('[Analytics] s_md.wb.cstmids:', s_md.wb && s_md.wb.cstmids);
        console.log('[Analytics] s_md.wb.ldcncptid:', s_md.wb && s_md.wb.ldcncptid);
    }
}

// ============================================================================
// END ANALYTICS SECTION
// ============================================================================

// Initialize global toggle function for Key Points cards
window.toggleKeyPoints = function(element) {
    var card = element.closest('.key-points-card');
    if (!card) {
        return;
    }
    
    var content = card.querySelector('.key-points-content');
    var chevronDown = card.querySelector('.chevron-down');
    var chevronUp = card.querySelector('.chevron-up');
    var header = card.querySelector('.key-points-header');
    var isExpanded = card.getAttribute('data-expanded') === 'true';
        
    if (isExpanded) {
        // Collapse
        card.classList.remove('expanded');
        card.classList.add('collapsed');
        card.setAttribute('data-expanded', 'false');
        if (header) header.setAttribute('aria-expanded', 'false');
        
        if (content) {
            content.style.setProperty('display', 'none', 'important');
        }
        
        if (chevronDown) chevronDown.style.display = 'block';
        if (chevronUp) chevronUp.style.display = 'none';
    } else {
        // Expand
        card.classList.remove('collapsed');
        card.classList.add('expanded');
        card.setAttribute('data-expanded', 'true');
        if (header) header.setAttribute('aria-expanded', 'true');
        
        if (content) {
            content.style.setProperty('display', 'flex', 'important');
        }
        
        if (chevronDown) chevronDown.style.display = 'none';
        if (chevronUp) chevronUp.style.display = 'block';
    }
};

var ResponseTypes = {
    MESSAGE: 'message',
    MESSAGE_END: 'message_end',
    ERROR: 'error',
    WORKFLOW_START: 'workflow_started',
    WORKFLOW_END: 'workflow_finished',
    NODE_START: 'node_started',
    NODE_END: 'node_finished'
};

function toggleProgressText(text) {
    const progressTexts = document.querySelectorAll('.ai-search-progress, .ai-search-progress .progress-text-b, .ai-search-progress .progress-text-c, .dot-flashing');
    progressTexts.forEach((textEl) => {
      textEl.classList.remove('active');
    });
	if(!text || text === 'remove') {
		document.querySelector('.ai-search-progress').classList.remove('active');
		document.querySelector('.ai-search-asking .ai-search-progress').classList.remove('active');
	} else if(text === 'dot-flashing') {
		if (document.querySelector('.ai-search-asking-question') != null) {
			document.querySelector('.ai-search-asking .ai-search-progress').classList.add('active');
			document.querySelector('.ai-search-asking .ai-search-progress .dot-flashing').classList.add('active');
		} else {
			document.querySelector('.ai-search-progress').classList.add('active');
			document.querySelector('.ai-search-progress .dot-flashing').classList.add('active');
		}
	} else {
		if (document.querySelector('.ai-search-asking-question') != null) {
			document.querySelector('.ai-search-asking .ai-search-progress').classList.add('active'); 
			document.querySelector('.ai-search-asking .ai-search-progress').classList.add('active');
			document.querySelector(`.ai-search-asking .ai-search-progress .progress-text-${text}`).classList.add('active');
		} else {
			document.querySelector('.ai-search-progress').classList.add('active');
			var textEl = document.querySelector(`.ai-search-progress .progress-text-${text}`);
			if(textEl) {
				textEl.classList.add('active');
			}
		}
	}
}

function scrollToBottom(event) {
document.querySelector('.ai-search-more').classList.remove('active');

// Mark ALL programmatic scrolling as automatic to prevent analytics tracking
// This includes clicking the "More" button - it's a programmatic scroll, not manual scrolling
_vars.userScrollNoTrack = true;
clearTimeout(_vars.userScrollNoTrackTimer);
var modalContent = document.querySelector('.ai-search-content');
    modalContent.scrollTo({
        top: modalContent.scrollHeight,
        behavior: 'smooth'
    });
        // Clear the flag after scrolling completes
        _vars.userScrollNoTrackTimer = setTimeout(function(){
            _vars.userScrollNoTrack = false;
        }, 2000);
}

function scrollQuestionToTop() {
    // Mark as automatic scroll to prevent analytics tracking
    _vars.userScrollNoTrack = true;
    clearTimeout(_vars.userScrollNoTrackTimer);
	try {
		var currentQuestion = document.querySelector('.ai-search-question.current, .ai-search-asking-question.current');
		if (!currentQuestion) {
			console.log('No current question found');
               _vars.userScrollNoTrack = false;
			return;
		}
		
		var scrollContainer = document.querySelector('.ai-search-content');
		
		
		if (!scrollContainer) {
			console.log('No scroll container found');
               _vars.userScrollNoTrack = false;
			return;
		}

		var viewportHeight = scrollContainer.clientHeight;
		
		var existingSpacer = scrollContainer.querySelector('.temp-scroll-spacer');
		if (existingSpacer) {
			existingSpacer.remove();
		}

		var fullWidthEl = document.querySelector('.ai-search-full-width-container');
		
		var spacerHeight = viewportHeight-(currentQuestion.offsetHeight+ (fullWidthEl ? 110 : 130));
		
		var spacer = document.createElement('div');
		spacer.className = 'temp-scroll-spacer';
		spacer.style.width = '1px';
		spacer.style.visibility = 'hidden';
		spacer.style.pointerEvents = 'none';
		spacer.style.padding = spacerHeight + 'px 0px 0px 0px';
		scrollContainer.appendChild(spacer);

		setTimeout(function() {
			currentQuestion.scrollIntoView({
				block: 'start',
				inline: 'nearest',
				behavior: 'smooth'
			});
		}, 100);

		var moreButton = document.querySelector('.ai-search-more');
		if (moreButton) {
			moreButton.classList.remove('active');
		}

        // Clear the flag after scrolling completes
        _vars.userScrollNoTrackTimer = setTimeout(function(){
            _vars.userScrollNoTrack = false;
        }, 1000);

	} catch (error) {
		var fallbackQuestion = document.querySelector('.ai-search-question.current, .ai-search-asking-question.current');
		if (fallbackQuestion) {
			fallbackQuestion.scrollIntoView({block: 'start', inline: 'nearest', behavior: 'smooth'});
		}
                // Clear the flag even on error
        _vars.userScrollNoTrackTimer = setTimeout(function(){
            _vars.userScrollNoTrack = false;
        }, 1000);
	}
}

function hideHeader() {
	if (!autoScrolledOnce && _vars.isFullPage) {
		document.querySelectorAll('p.intro, p.intro + p').forEach(function(el) {
			el.classList.add('fade-out');
			setTimeout(function(){
				el.classList.add('hidden');
			}, 400);
		});
	}
}

function handleDifyContentScroll() {
	var modalContent = document.querySelector('.ai-search-content');
	var isAtBottom = modalContent.scrollHeight - modalContent.scrollTop <= modalContent.clientHeight + 10;
	var spacerEl = document.querySelector('.temp-scroll-spacer');
	if (isAtBottom) {
		document.querySelector('.ai-search-more').classList.remove('active');
	} else if(!document.querySelector('.ai-search-more').classList.contains('active') && (!spacerEl || !_vars.pending)) {
		document.querySelector('.ai-search-more').classList.add('active');
	}
}

function handleFollowUpScroll() {
	var modalContent = document.querySelector('.ai-search-content');
	var isAtBottom = modalContent.scrollHeight - modalContent.scrollTop <= modalContent.clientHeight + 10;
	if (isAtBottom) {
		document.querySelector('#ai-search-modal').classList.add('prompts-on');
		document.querySelector('.prompts-desktop-modal').scrollLeft = 0;
		document.querySelector('.prompts-mobile-modal').scrollLeft = 0;	
		document.querySelector('.ai-search-content').removeEventListener('scroll', handleFollowUpScroll);
		scrollToBottom();
	}
}

function clearFollowUps() {	// ToDo - consolidate desktop/mobile prompt containers in modal
	document.querySelector('.ai-search-content').removeEventListener('scroll', handleFollowUpScroll);
	document.querySelector('#ai-search-modal').classList.remove('prompts-on');
	document.querySelector('.prompts-desktop-modal').innerHTML = '';	
	document.querySelector('.prompts-mobile-modal').innerHTML = '';
}

function renderFollowUps(followUpObj) {		

	clearFollowUps();
	
	followUpObj['follow_up_questions'].forEach(function(fQ, idx) {
		document.querySelector('.prompts-desktop-modal').insertAdjacentHTML('beforeend', _html.followUp(fQ.question, idx));
		document.querySelector('.prompts-mobile-modal').insertAdjacentHTML('beforeend', _html.followUp(fQ.question, idx));
	});
	
	document.querySelectorAll('.prompts-desktop-modal span, .prompts-mobile-modal span').forEach(function(followUp) {
		followUp.addEventListener('click', handleSearch);
	});
	
	document.querySelector('.ai-search-content').addEventListener('scroll', handleFollowUpScroll);
	
}

function toggleDrawer(open) {
    var drawer = document.querySelector('.drawer');
    var form = document.querySelector('#feedbackForm');
    var thankYou = document.querySelector('#feedbackSuccessMsg');
    var otherText = document.querySelector('#otherText');
	var fullWidthEl = document.querySelector('.ai-search-full-width-container');
    if(open) {
        thankYou.style.display = 'none';
		form.style.display = 'block';
        drawer.classList.add('active');
		if(fullWidthEl) {
			fullWidthEl.classList.add('show-overlay');
		}
    } else {
        drawer.classList.remove('active');
        form.reset();
        otherText.style.display = 'none';
		thankYou.style.display = 'none';
		if(fullWidthEl) {
			fullWidthEl.classList.remove('show-overlay');
		}
    }
}

// Delegated like/dislike listeners (robust against async DOM updates)
var _delegatedRatingInit = false;
function initDelegatedRatingListeners() {
    if (_delegatedRatingInit) { return; }
    _delegatedRatingInit = true;
    document.addEventListener('click', function(e) {
        var target = e.target;
        if (!target || !(target.classList && (target.classList.contains('ai-search-like') || target.classList.contains('ai-search-dislike') || target.classList.contains('ai-search-copy')))) {
            return;
        }
		if(target.classList.contains('ai-search-copy')) {
			copyConversation.handleCopyConversation(e);
			return;
		}
        var ratingContainer = target.closest('.ai-search-rating');
        if (!ratingContainer) { return; }
        var wasLikeSelected = !!ratingContainer.querySelector('.ai-search-like.selected');
        var wasDislikeSelected = !!ratingContainer.querySelector('.ai-search-dislike.selected');
        var isDislike = target.classList.contains('ai-search-dislike');
        var isLike = target.classList.contains('ai-search-like');
        // Clear and set selection in this container
        ratingContainer.querySelectorAll('.ai-search-like, .ai-search-dislike').forEach(function(btn){ btn.classList.remove('selected'); });
        target.classList.add('selected');
        // Transition analytics
        if (isLike && wasDislikeSelected && typeof wmdPageLink !== 'undefined') {
            wmdPageLink('ai-search_dislike-to-like', target);
        }
        if (isDislike && wasLikeSelected && typeof wmdPageLink !== 'undefined') {
            wmdPageLink('ai-search_like-to-dislike', target);
        }
        // Core analytics/events
        if (isLike) {
            var eventData = { eventProperties: { likeStatus: 'like' } };
            difyApi.sendEvent(eventData);
            if (typeof wmdPageLink !== 'undefined') { wmdPageLink('ai-search_like', target); }
        } else if (isDislike) {
            if (typeof window !== 'undefined') { window.currentDislikeElement = target; }
            toggleDrawer(true);
            if (typeof wmdPageLink !== 'undefined') { wmdPageLink('ai-search_dislike', target); }
        }
    }, true);
}

function applyInlineMarkdown(text) {
    if (!text || typeof text !== 'string') return text;
    // Convert bold **text**
    let processed = text.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
    // Convert italic *text* that is not part of bold
    processed = processed.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, function(m, pre, content){
        return pre + '<i>' + content + '</i>';
    });
    return processed;
}

function renderAnswer(answer, isFirstRender, summaryBullets) {
	
	// Normalize markdown inline styles before rendering
	answer = applyInlineMarkdown(answer);
	
	// If it's the first/only question + answer, append to different container
	if(isFirstRender || _vars.provider !== 'dify') {
        if (_vars.provider === 'dify') {
            // PROFCORE-34567: renderQuestion() already inserted the answer container for this
            // question (ads.js may have already placed Ad 2 inside it). Fill that container in
            // place instead of inserting a brand-new one here, which used to create a second
            // .ai-search-answer block and strand Ad 2 in the empty first one.
            var answerBlocks = document.querySelectorAll('.ai-search-answer.active');
            if (answerBlocks.length > 0) {
                var lastAnswerBlock = answerBlocks[answerBlocks.length - 1];
                var answerDiv = lastAnswerBlock.querySelector('.answer');
                var answerContentEl = answerDiv ? answerDiv.querySelector('.answer-content') : null;
                if (answerContentEl) {
                    if (summaryBullets && Array.isArray(summaryBullets) && summaryBullets.length > 0) {
                        answerContentEl.insertAdjacentHTML('beforebegin', _html.keyPointsCard(summaryBullets));
                    }
                    answerContentEl.insertAdjacentHTML('beforeend', answer);
                }
            }
        } else if (document.querySelector('.ai-search-asking-question') != null) {
            document.querySelector('.ai-search-asking .ai-search-progress').insertAdjacentHTML('beforebegin', _html.showAnswer(answer, summaryBullets));
        } else {
            document.querySelector('.ai-search-progress').insertAdjacentHTML('beforebegin', _html.showAnswer(answer, summaryBullets));
        }

        // Once Answer there, scroll question to top - mark as automatic scroll
        _vars.userScrollNoTrack = true;
        clearTimeout(_vars.userScrollNoTrackTimer);
        
        // Check if this is a full-width page
        var fullWidthEl = document.querySelector('.ai-search-full-width-container');
        var currentQuestion = document.querySelector('.ai-search-question.current, .ai-search-asking-question.current');
        
        // For modal, scroll to question. For full-width, don't scroll - ads.js handles it
        if (!fullWidthEl && currentQuestion) {
            currentQuestion.scrollIntoView({block: 'start', inline: 'nearest', behavior: 'smooth'});
        }
        
        _vars.userScrollNoTrackTimer = setTimeout(function(){
            _vars.userScrollNoTrack = false;
        }, 500);
				if(_vars.provider !== 'dify') {
			// Add rating buttons immediately after answer for non-dify provider
			var answerBlocks = document.querySelectorAll('.ai-search-answer.active');
			if (answerBlocks.length > 0) {
				var lastAnswerBlock = answerBlocks[answerBlocks.length - 1];
				var answerDiv = lastAnswerBlock.querySelector('.answer');
				if (answerDiv && !answerDiv.querySelector('.ai-search-rating')) {
					var answerContent = answerDiv.querySelector('.answer-content');
					if (answerContent) {
						answerContent.insertAdjacentHTML('afterend', _html.showRating());
					}
				}
			}
			document.querySelector('.ai-search-new').classList.add('active');
			document.querySelector('#ai-search-input-modal').disabled = false;
		}
	} else if(_vars.provider === 'dify') {
		var answerContentNodes = document.querySelectorAll('.ai-search-answer.active .answer .answer-content');
		if(answerContentNodes.length) {
			var lastAnswer = answerContentNodes[answerContentNodes.length-1];
			// Use insertAdjacentHTML to preserve existing DOM elements (including citations)
			lastAnswer.insertAdjacentHTML('beforeend', applyInlineMarkdown(answer));
			var spacerEl = document.querySelector('.temp-scroll-spacer');
			if(spacerEl) {
				var spacerHeight = spacerEl.clientHeight;
				if(lastAnswer.clientHeight >= spacerHeight) {
					spacerEl.parentNode.removeChild(spacerEl);
				} else {
					spacerEl.padding = (spacerHeight - lastAnswer.clientHeight) + 'px 0px 0px 0px';
				}
			}
		}
	}
	
}

function handleStopGenerate() {
	if(_vars.provider === 'dify') {
		clearTimeout(_vars.progressBTimer);
		clearTimeout(_vars.progressCTimer);
		difyApi.stopGenerate();
	}
	clearTimeout(_vars.socketTimer);
	document.querySelector('.ai-search-stop').classList.remove('active');
	wmdPageLink('ai-search_stp-gen', this);

    var currQuestion = document.querySelector('.ai-search-question.current, .ai-search-asking-question.current');
	_vars.pending = false;
	currQuestion.classList.remove('loading');
    currQuestion.classList.add('failed');
	currQuestion.addEventListener('click', handleRetry);
	toggleProgressText('remove');
    document.querySelector('#ai-search-input-modal').disabled = false;
}

function handleDifyStopGenerate() {
	clearTimeout(_vars.socketTimer);
	clearTimeout(_vars.progressBTimer);
	clearTimeout(_vars.progressCTimer);
	difyApi.stopGenerate();
	document.querySelector('.ai-search-stop-btn-dify').classList.remove('active');
	wmdPageLink('ai-search_stp-gen-button', this);
	_vars.pending = false;
	toggleProgressText('remove');
    document.querySelector('#ai-search-input-modal').disabled = false;

}

// Silently abort an in-flight AI query when the modal is closed. Same teardown as
// handleDifyStopGenerate (aborts the browser fetch + tells the server to stop generating),
// but WITHOUT the ai-search_stp-gen-button analytics, since a modal close isn't a stop click.
// ARTICLE PAGES ONLY - other surfaces keep their existing close behavior untouched. No-ops
// when nothing is in flight, so it's safe to call on every article-page close.
function abortActiveQuery() {
	if (!_vars.isArticlePage) {
		return;
	}
	var stopBtn = document.querySelector('.ai-search-stop-btn-dify');
	var inFlight = _vars.pending || (stopBtn && stopBtn.classList.contains('active'));
	if (!inFlight) {
		return;
	}
	clearTimeout(_vars.socketTimer);
	clearTimeout(_vars.progressBTimer);
	clearTimeout(_vars.progressCTimer);
	if (_vars.provider === 'dify') {
		difyApi.stopGenerate();
	}
	if (stopBtn) {
		stopBtn.classList.remove('active');
	}
	_vars.pending = false;
	toggleProgressText('remove');
	var input = document.querySelector('#ai-search-input-modal');
	if (input) {
		input.disabled = false;
	}
}

function handleRetry(event) {
	this.remove();
	handleSearch(event);
	document.querySelector('.ai-search-stop').classList.remove('active');
}

function switchToConversationPage(fullWidthEl) {
	var searchEl = document.querySelector('#ai-search-input-modal');
	fullWidthEl.classList.remove('animate');
	fullWidthEl.classList.remove('animate-out');
	fullWidthEl.classList.add('has-question-rendered');
	fullWidthEl.classList.remove('landing-page');
	fullWidthEl.classList.remove('mobile-card-active');
	fullWidthEl.classList.add('bg-white');
	fullWidthEl.classList.add('fixed-height');
	fullWidthEl.classList.add('scrollable-header-layout')

	// placing the header logo inside ai-search content so that it scrolls along with the content
	var aiSearchContent = document.querySelector('.ai-search-content-maxwide')
	var aiSearchContentHeader = document.querySelector('.ai-search-content-header')
	if(aiSearchContent && !aiSearchContentHeader){
		aiSearchContent.insertAdjacentHTML('afterbegin', _html.scrollableHeaderLayoutHeader())
	}

	document.querySelector('.suggested-container').classList.remove('active');
	if(document.querySelector('.history-panel') !== null) {
		document.querySelector('.history-panel').classList.remove('active');
        document.querySelector('.history_btn').style.display = 'flex';
	}
	searchEl.blur();
	window.history.pushState({view: 'chatinterface'}, '');
}

function handleSearch(event) {
    // A2A feeds: capture the current article's context (guid/title/content-type) before the
    // stream fires. This is the single reliable capture point — every search path funnels
    // through here, including the Vue deep-dive links which call window.aiQueryHandleSearch
    // (= handleSearch) directly and bypass the click listener in bindui. captureA2AArticleContext
    // is registered on _vars by bindui to avoid a circular module dependency.
    // Passed as a fallback (second arg): it must not overwrite the context an explicit caller
    // (deep-dive button/pill, FAB) already captured for this query — see PROFCORE-34511.
    if (_vars.isArticlePage && typeof _vars.captureA2AArticleContext === 'function') {
        _vars.captureA2AArticleContext(null, true);
    }

    // Reset querySource at the start of each new query unless it was just set by suggested prompts
    // The isInitialPromptQuery flag is set by suggested-prompts.js right before calling handleSearch
    // NOTE: isInitialPromptQuery is load-bearing here even though querySource is also pre-set in
    // suggested-prompts.js / bindui.js. Without the flag, the else-branch below would reset
    // querySource to undefined before getMsgSubmitMethod() reads it. Do not remove the flag.
    if (_vars.isInitialPromptQuery === true) {
        _vars.querySource = 'Initial prompt';
        _vars.isInitialPromptQuery = false; // Reset the flag after using it
    } else if (_vars.querySource === 'ai-mode-query' && _vars.isUrlPredefinedQuery) {
        // Preserve 'ai-mode-query' only while the initial PDQ stream is still active.
        // Once dify/index.js resets isUrlPredefinedQuery=false (stream end/error),
        // this guard fails and querySource is cleared so follow-up queries are correctly
        // tagged as 'organic' or 'flwup' instead of leaking 'ai-mode-query'.
    } else {
        // Reset to undefined so it gets properly set based on actual source
        _vars.querySource = undefined;
    }
	var fullWidthEl = document.querySelector('.ai-search-full-width-container');
    // For PDQ (Predefined Query) flows, refresh PVID immediately before any ads are fired
    // This ensures both Ad 1 (fired in renderQuestion) and msg-sent Analytics use the same PVID
    // MEDAIS-1999: Fixes PVID mismatch between Ads calls and Virtual Pageview events
    if (_vars.isUrlPredefinedQuery && typeof GenPVID !== 'undefined') {
        GenPVID();
    }

	// Check if query should be gated for unknown users
	
	if (aiGating.shouldGateAIQuery()) {
		if (fullWidthEl) {
			document.querySelector('#ai-search-input-modal').value = '';
			document.querySelector('#ai-search-modal-submit').classList.remove('visible');
			switchToConversationPage(fullWidthEl);
		} else {
			if (!document.body.classList.contains('ai-search-modal-maximized')) {
				document.body.classList.add('ai-search-modal-maximized');
			}
			document.body.classList.add('show-ai-search-modal');
			document.querySelector('.ai-search-footer').classList.add('inactive');
		}
		// Show login gate instead of processing query
		aiGating.showAIGate();
		scrollToBottom();
		// Query never reaches the provider, so clear any pending suppression flag here
		// to avoid it leaking into the next query the user sends after logging in.
		_vars.suppressKeyPointsOnce = false;
		return;
	}

	// Remove preserved context error if present
	var preservedError = document.querySelector('.preserved-context-error');
	if (preservedError) {
		preservedError.remove();
	}
	var aiSearchStopActive =  document.querySelector('.ai-search-stop').classList.contains('active');

    var difyStopActive = document.querySelector('.ai-search-stop-btn-dify') && document.querySelector('.ai-search-stop-btn-dify').classList.contains('active');

	if (difyStopActive) {
		handleStopGenerate();
	}

	if ((!_vars.pending && !_vars.draggedFlag) || aiSearchStopActive) { // No query already pending
		if(typeof event === 'string') {
            // Handle string input - determine source based on context
            // querySource is either already set to 'Initial prompt' (from flag at start of handleSearch)
            // or it's undefined (reset at start of handleSearch)
            if (_vars.querySource === 'Initial prompt') {
            // Already set by the flag - keep it
            } else if (_vars.querySource === 'ai-mode-query' && _vars.isUrlPredefinedQuery) {
            // Already set by site-search AI Mode detection in bindui.js - keep it while PDQ stream is active
            } else if (_vars.isUrlPredefinedQuery) {
            _vars.querySource = 'PDQ';
            } else {
            // Default to follow-up question for string inputs (most common case)
            _vars.querySource = 'Follow up question';
            }
			// Handle string input (from follow-up questions) for both full-width and modal
			if (fullWidthEl) {
				switchToConversationPage(fullWidthEl);
			} else {
				// For non-full-width pages (article pages, condition pages), ensure modal is open
				if (!document.body.classList.contains('show-ai-search-modal')) {
					document.body.classList.add('show-ai-search-modal');
				}
				// Hide the sticky input wrapper for article pages
				var articleSearchWrapper = document.querySelector('.article-search-wrapper');
				if (articleSearchWrapper) {
					articleSearchWrapper.style.display = 'none';
				}
			}
			_vars.pending = true;
			renderQuestion(event);
                // Check if this is a predefined query from URL parameters
                if (_vars.isUrlPredefinedQuery) {
                    // Use wrapper to set context, call analytics, and remove context
                    setQueryWordCount(event);
                    var attrs = getAnalyticsAttributes();
                    withAnalyticsContext('msg-pdq-submit', function() {
                    wmdPageLink('ai-search_msg-pdq-submit');
                    }, {
                        queryWordCount: attrs.queryWordCount,
                        leadConceptId: attrs.leadConceptId,
                        threadId: attrs.threadId,
                        runId: attrs.runId
                    });
                    // NOTE: Do NOT reset _vars.isUrlPredefinedQuery here.
                    // It will be reset after stream completes in dify/index.js (event.done)
                    // so URL scg is used for ALL analytics and ad calls throughout the PDQ query lifecycle.
                } else {
                    // Fire msg-submit immediately with available data (no waiting for API response)
                    // For first query: only queryWordCount in cstmattr, no cstmids
                    // For subsequent queries: queryWordCount in cstmattr, thread_id in cstmids
                    setQueryWordCount(event);
                    withAnalyticsContext('msg-submit', function() {
                        wmdPageLink('ai-search_msg-submit');
                    }, {
                        queryWordCount: getAnalyticsAttributes().queryWordCount,
                        // For article pages (A2A), use analyticsState.threadId which respects resetAnalyticsAttributes() on article switch
                        // For non-article pages, use _vars.conversationId directly
                        threadId: _vars.isArticlePage ? getAnalyticsAttributes().threadId : (_vars.conversationId || 'no-val'),
                        runId: 'no-val',  // Don't pass run_id for msg-submit
                        leadConceptId: getAnalyticsAttributes().leadConceptId
                    });
                }
		} else if (typeof event.key == 'undefined' || event.key == 'Enter') { // Click, or enter-key
			if (aiSearchStopActive) {
				handleStopGenerate();
			}

			//Animation changes when the query is sent
			
			if(fullWidthEl) {
				switchToConversationPage(fullWidthEl);
			} else {
				// For non-full-width pages (article pages, condition pages), ensure modal is open
				if (!document.body.classList.contains('show-ai-search-modal')) {
					document.body.classList.add('show-ai-search-modal');
				}
				// Hide the sticky input wrapper for article pages
				var articleSearchWrapper = document.querySelector('.article-search-wrapper');
				if (articleSearchWrapper) {
					articleSearchWrapper.style.display = 'none';
				}
			}

					// Updated to handle both input and textarea elements
			var thisInput = event.target.parentElement.querySelector('input[type=text], textarea');
			if((thisInput == null || !thisInput.value) && !event.target.innerText) {
				return;
			}
			_vars.pending = true;
			if (thisInput == null) { // No Custom Input, Clicked a Suggested Question
            if (event.target.closest('.suggested-card') || event.target.closest('.ai-search-prompts') || event.target.classList.contains('prompt-item')) {
            _vars.querySource = 'Initial prompt';
            } else if (event.target.closest('.prompts-desktop-modal') || event.target.closest('.prompts-mobile-modal') || event.target.closest('.follow-up-questions-section') || event.target.classList.contains('follow-up-question-item')) {
            _vars.querySource = 'Follow up question';  
            } else {
            _vars.querySource = 'Organic';
            }


				renderQuestion(event.target.innerText);
			} else { // Custom Input Field Question
            // Set query source for typed queries
            if (_vars.querySource === 'ai-mode-query' && _vars.isUrlPredefinedQuery) {
            // Already set by site-search AI Mode detection in bindui.js - keep it while PDQ stream is active
            } else if (_vars.isUrlPredefinedQuery) {
            _vars.querySource = 'PDQ';
            } else {
            _vars.querySource = 'Organic';
            }
				// Save the input value BEFORE clearing it
                var queryText = thisInput.value;
                renderQuestion(queryText);
				thisInput.value = '';
				//  clear the modal textarea specifically after query gets submitted.
				var modalTextarea = document.querySelector('#ai-search-input-modal');
				if (modalTextarea && modalTextarea !== thisInput) {
					modalTextarea.value = '';
				}
				if (event.target.id == 'ai-search-input-modal' || event.target.id == 'ai-search-modal-submit') { // Modal layer input
					if (fullWidthEl) {
						document.querySelector('#ai-search-modal-submit').classList.add('disabled');
					}
                    // Fire msg-submit immediately with available data (no waiting for API response)
                    // For first query: only queryWordCount in cstmattr, no cstmids
                    // For subsequent queries: queryWordCount in cstmattr, thread_id in cstmids
                    setQueryWordCount(queryText);
                    withAnalyticsContext('msg-submit', function() {
                    wmdPageLink('ai-search_msg-submit', event.target);
                    }, {
                        queryWordCount: getAnalyticsAttributes().queryWordCount,
                        // For article pages (A2A), use analyticsState.threadId which respects resetAnalyticsAttributes() on article switch
                        // For non-article pages, use _vars.conversationId directly
                        threadId: _vars.isArticlePage ? getAnalyticsAttributes().threadId : (_vars.conversationId || 'no-val'),
                        runId: 'no-val',  // Don't pass run_id for msg-submit
                        leadConceptId: getAnalyticsAttributes().leadConceptId
                    });
				} else {
					wmdTrack('ai-search_msg-type-submit', event.target); // Input from main page
					aiSchPageview(0);
				}
			}
		}
	}
}

window.aiQueryHandleSearch = handleSearch;

function renderQuestion(question) {
	
        // Reset scroll tracking for new query
    if (_vars.resetScrollTracking && typeof _vars.resetScrollTracking === 'function') {
    _vars.resetScrollTracking();
    }
    
    // Reset query-specific analytics values for new query
    // This ensures values from previous queries don't carry forward
    resetQueryAttributes();
    analyticsState.messageIntent = 'no-val'; // Reset intent for new query
    // Reset lead concept only for non-PDQ queries (PDQ queries preserve URL-provided values)
    if (!_vars.isUrlPredefinedQuery) {
        analyticsState.leadConceptId = 'no-val';
        _vars.leadConcept = null;
    }
    
    // Hide 'Start New' and related message
    document.querySelector('.ai-search-new').classList.remove('active');
    document.querySelector('.ai-search-instructions').classList.remove('active');
	
	// Hide Hardcoded / Follow-ups
	document.querySelector('.ai-search-content .ai-search-prompts').classList.remove('active');
	clearFollowUps();

// Disable AI Search Inputs
    document.querySelector('#ai-search-input-modal').disabled = true;
	document.querySelector('#ai-search-input-modal').placeholder = 'Ask follow up';
	
	document.querySelector('#ai-search-field-modal').classList.remove('has-content');
	
	document.querySelector('#ai-search-modal-submit').classList.remove('visible');
	
	// Show Layer
	document.body.classList.add('show-ai-search-modal');

	// A rendered question always means the conversation view, never the minimized
	// FAB landing state. Clear any leftover minimized state (e.g. a FAB open that was
	// closed and reopened via a deep dive link) and force maximized so the modal
	// renders at full height instead of the minimized `height: auto` layout.
	if (_vars.isArticlePage) {
		document.body.classList.remove('ai-search-modal-minimized');
		if (!document.body.classList.contains('ai-search-modal-maximized')) {
			document.body.classList.add('ai-search-modal-maximized');
		}
	}

	// If last question failed, leave it there for the retry, but specify it's not current question for rest of code
	if (document.querySelector('.ai-search-question.current, .ai-search-asking-question.current') != null) {
		document.querySelector('.ai-search-question.current, .ai-search-asking-question.current').classList.remove('current');
	}
	
	// If pending search timing out, stop it before doing new one
	if (document.querySelector('.ai-search-stop').classList.contains('active')) {
		handleStopGenerate();
	}
		// If it's the first/only question, append to different container
	var isFirstQuestion = document.querySelector('.ai-search-question') == null;
	if (isFirstQuestion) {
		document.querySelector('.ai-search-progress').insertAdjacentHTML('beforebegin', _html.askingQuestion(question));
	} else {
		document.querySelector('.ai-search-asking .ai-search-progress').insertAdjacentHTML('beforebegin', _html.askingQuestion(question));
	}
	// Store reference to the newly created question element for potential ad insertion
    _vars.lastCreatedQuestion = document.querySelector('.ai-search-question.current, .ai-search-asking-question.current');
    
    // PROFCORE-34567: Create empty answer block immediately after question
    // This ensures the answer block exists when batched ad call happens
    var emptyAnswerHtml = '<div class="ai-search-answer active"><div class="answer"><div class="answer-content"></div></div></div>';
    if (document.querySelector('.ai-search-asking-question') != null) {
        document.querySelector('.ai-search-asking .ai-search-progress').insertAdjacentHTML('beforebegin', emptyAnswerHtml);
    } else {
        document.querySelector('.ai-search-progress').insertAdjacentHTML('beforebegin', emptyAnswerHtml);
    }

	var isFirstQuestion = document.querySelectorAll('.ai-search-question, .ai-search-asking-question').length <= 1;
    // Once Answer there, scroll question to appropriate position - mark as automatic scroll
    _vars.userScrollNoTrack = true;
    clearTimeout(_vars.userScrollNoTrackTimer);
	if (isFirstQuestion) {
		// First-message flow remains unchanged - use original scrollIntoView behavior
		document.querySelector('.ai-search-question.current, .ai-search-asking-question.current').scrollIntoView({block: 'start', inline: 'nearest', behavior: 'smooth'});
         _vars.userScrollNoTrackTimer = setTimeout(function(){
        _vars.userScrollNoTrack = false;
            }, 500);
	} else {
		// For follow-up queries, add a small delay to ensure DOM is fully updated before scrolling
		setTimeout(function() {
			scrollQuestionToTop();
		}, 100);
	}
// Show Ad 1 immediately for PDQ queries only (URL scg takes priority).
    // For normal queries, Ad 1 is triggered from the metrics event in dify/index.js
    // after the API returns the concept_id. This prevents double ad calls on follow-up
    // queries where renderQuestion fires for a normal query but dify/index.js also fires.
    if (_vars.isUrlPredefinedQuery) {
        ads.showAd1OnQuery('url');
    }
	
	_vars.searchResult = {
		question: null,
		answerId: null,
		answer: '',
		responseTime: null,
		responseTimeFirstToken: null
	}
	var provider = getCurrentProvider();
	if(provider) {
		provider.sendQuery(question);
	}
}

function getConversationHistory(conversationId) {
    return new Promise(function(resolve, reject) {
        difyApi.getHistory(conversationId)
            .then(function(result) {
                resolve(result);
            })
            .catch(function(err) {
                reject(err);
            });
    });
}

function updateProgressStep(stepText) {
    var container = document.querySelector('.ai-search-asking-question') ? 
        document.querySelector('.ai-search-asking .ai-search-progress') : 
        document.querySelector('.ai-search-progress');
    if(!container) { return; }
    var txtEl = container.querySelector('.progress-text-a');
    if(stepText) {
        _vars.lastStep = stepText; // persist last step
    }
    var displayText = stepText || _vars.lastStep;
    if(!displayText) { return; }
    
    // Show progress container and text when stepText is present
    container.classList.add('active');
    if(txtEl){ 
        txtEl.innerText = displayText; 
        txtEl.classList.add('active'); 
    }
}

function processTablesInDOM() {
    var answerElements = document.querySelectorAll('.ai-search-answer.active .answer-content');
    
    answerElements.forEach(function(answerEl) {
        var content = answerEl.innerHTML;
        
        // Skip if already has a table
        if (answerEl.querySelector('table')) {
            return;
        }
        
        try {
            var bindui = require('./bindui');
            
            // Process HTML entities first (always)
            var processedContent = processHTMLEntities(content);
            
            // Then process markdown tables if any
            processedContent = bindui.processMarkdownTables(processedContent);
            
            // Apply inline markdown for bold/italic
            processedContent = applyInlineMarkdown(processedContent);
            
            if (processedContent !== content) {
                answerEl.innerHTML = processedContent;
            }
        } catch (error) {
            console.error('Error processing content:', error);
        }
    });

    // Initialise sticky headers for any newly rendered tables
    initStickyTableHeaders();
}

function initStickyTableHeaders() {
    var modalContent = document.querySelector('.ai-search-content');
    if (!modalContent) return;

    var containers = document.querySelectorAll('.table-container:not([data-sticky-init])');
    containers.forEach(function(container) {
        container.setAttribute('data-sticky-init', '1');

        var table = container.querySelector('table');
        var thead = container.querySelector('thead');
        if (!table || !thead) return;

        // Create floating sticky header element
        var stickyEl = document.createElement('div');
        stickyEl.className = 'sticky-table-header';
        // z-index must exceed #ai-search-modal (z-index_layer + 497 = 6000497)
        stickyEl.style.cssText = 'display:none;position:fixed;overflow:hidden;z-index:6000498;pointer-events:none;box-sizing:border-box;background:#fff;border-bottom:2px solid #c5ced3;';

        var cloneTable = document.createElement('table');
        cloneTable.setAttribute('role', 'table');
        cloneTable.style.cssText = 'border-collapse:collapse;table-layout:fixed;font-size:0.875rem;line-height:1.4;background:#fff;word-break:auto-phrase;white-space:normal;';

        var cloneThead = thead.cloneNode(true);
        cloneTable.appendChild(cloneThead);
        stickyEl.appendChild(cloneTable);
        document.body.appendChild(stickyEl);

        function syncAndPosition() {
            var modalRect = modalContent.getBoundingClientRect();
            var containerRect = container.getBoundingClientRect();
            var theadRect = thead.getBoundingClientRect();
            var tableRect = table.getBoundingClientRect();
            var headerHeight = theadRect.height || thead.offsetHeight;

            // Show when thead starts scrolling above the modal content top
            // and the table bottom is still within the visible area
            var shouldShow = theadRect.top < modalRect.top &&
                             tableRect.bottom > modalRect.top + headerHeight &&
                             containerRect.width > 0;

            if (!shouldShow) {
                stickyEl.style.display = 'none';
                return;
            }

            // Size & position the sticky wrapper
            stickyEl.style.display = 'block';
            stickyEl.style.top = modalRect.top + 'px';
            stickyEl.style.left = containerRect.left + 'px';
            stickyEl.style.width = containerRect.width + 'px';
            stickyEl.style.height = headerHeight + 'px';

            // Sync each th width from the real header
            var realThs = thead.querySelectorAll('th');
            var cloneThs = cloneThead.querySelectorAll('th');
            realThs.forEach(function(th, i) {
                if (cloneThs[i]) {
                    var thStyle = window.getComputedStyle(th);
                    var thWidth = th.getBoundingClientRect().width;
                    cloneThs[i].style.width = thWidth + 'px';
                    cloneThs[i].style.minWidth = thWidth + 'px';
                    cloneThs[i].style.maxWidth = thWidth + 'px';
                    cloneThs[i].style.boxSizing = thStyle.boxSizing;
                    cloneThs[i].style.padding = thStyle.padding;
                    cloneThs[i].style.fontWeight = thStyle.fontWeight;
                    cloneThs[i].style.fontSize = thStyle.fontSize;
                    cloneThs[i].style.lineHeight = thStyle.lineHeight;
                    cloneThs[i].style.letterSpacing = thStyle.letterSpacing;
                    cloneThs[i].style.color = thStyle.color;
                    cloneThs[i].style.textAlign = thStyle.textAlign;
                    cloneThs[i].style.background = thStyle.backgroundColor || '#fff';
                }
            });

            // Mirror horizontal scroll offset using translateX
            cloneTable.style.width = tableRect.width + 'px';
            cloneTable.style.transform = 'translateX(-' + container.scrollLeft + 'px)';
        }

        modalContent.addEventListener('scroll', syncAndPosition, { passive: true });
        container.addEventListener('scroll', syncAndPosition, { passive: true });
        window.addEventListener('resize', syncAndPosition, { passive: true });
        syncAndPosition();

        // Clean up when table is removed from DOM (new chat etc.)
        var cleanupObserver = new MutationObserver(function() {
            if (!document.body.contains(container)) {
                stickyEl.remove();
                cleanupObserver.disconnect();
            }
        });
        cleanupObserver.observe(document.body, { childList: true, subtree: true });
    });
}

function processHTMLEntities(text) {
    if (!text) return '';
    
    let processed = text;
    
    // Handle the specific issue: > symbols that should be line breaks
    // This happens when <br> tags get HTML-escaped and then displayed as >
    // processed = processed.replace(/&gt;/g, '<br>');
    
    // Handle complete <br> tags
    processed = processed.replace(/<br\s*\/?>/gi, '<br>');
    
    // Handle escaped HTML entities for <br> tags  
    processed = processed.replace(/&lt;br&gt;/gi, '<br>');
    processed = processed.replace(/&lt;br\/&gt;/gi, '<br>');
    processed = processed.replace(/&lt;br \/&gt;/gi, '<br>');
    
    // Handle other HTML entities that might cause display issues
    processed = processed.replace(/&lt;/g, '<');
    processed = processed.replace(/&gt;/g, '>');
    processed = processed.replace(/&amp;/g, '&');
    processed = processed.replace(/&quot;/g, '"');
    processed = processed.replace(/&#39;/g, "'");
    
    return processed;
}

function recordLatencyMsFromQuery(basePerformance) {
    // basePerformance - queryStart or queryEnd performance
    if(!basePerformance) return null
    return String(Math.round(performance.now() - basePerformance))
}

function getCurrentTimestamp () {
    return String(Date.now())
}

function appendV1Param(url) {
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'v1';
}

module.exports = {
	ResponseTypes: ResponseTypes,
    toggleProgressText: toggleProgressText,
    scrollToBottom: scrollToBottom,
    scrollQuestionToTop: scrollQuestionToTop,
    hideHeader: hideHeader,
    renderFollowUps: renderFollowUps,
    renderAnswer: renderAnswer,
    handleRetry: handleRetry,
    handleStopGenerate: handleStopGenerate,
	handleDifyStopGenerate: handleDifyStopGenerate,
	abortActiveQuery: abortActiveQuery,
    handleSearch: handleSearch,
    handleDifyContentScroll: handleDifyContentScroll,
    toggleDrawer: toggleDrawer,
    getConversationHistory: getConversationHistory,
    renderQuestion: renderQuestion,
    switchToConversationPage: switchToConversationPage,
    updateProgressStep: updateProgressStep,
    processTablesInDOM: processTablesInDOM,
    initStickyTableHeaders: initStickyTableHeaders,
    initDelegatedRatingListeners: initDelegatedRatingListeners,
    // Analytics functions
    setMetadataAttributes: setMetadataAttributes,
    setMetricsAttributes: setMetricsAttributes,
    setQueryWordCount: setQueryWordCount,
    setResponseWordCount: setResponseWordCount,
    setResponseTimeFirstToken: setResponseTimeFirstToken,
    getAnalyticsAttributes: getAnalyticsAttributes,
    getMsgSubmitMethod: getMsgSubmitMethod,
    resetAnalyticsAttributes: resetAnalyticsAttributes,
    resetQueryAttributes: resetQueryAttributes,
    setAnalyticsContext: setAnalyticsContext,
    removeAnalyticsContext: removeAnalyticsContext,
    withAnalyticsContext: withAnalyticsContext,
    setContextForEvent: setContextForEvent,
    verifyAdobeVariables: verifyAdobeVariables,
    recordLatencyMsFromQuery:recordLatencyMsFromQuery,
    getCurrentTimestamp:getCurrentTimestamp,
    appendV1Param: appendV1Param,
}
